import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer, displayFont } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

type TemplatesStrings = Pick<
  HomepageStrings,
  | 'templates_badge'
  | 'templates_h2_pre'
  | 'templates_h2_highlight'
  | 'templates_body'
  | 'templates_cta'
>;

const EN_DEFAULTS: TemplatesStrings = {
  templates_badge: 'Build Bespoke',
  templates_h2_pre: 'Designs your guests will ',
  templates_h2_highlight: 'talk about',
  templates_body: 'Animated, elegant invitation templates crafted for modern couples. Every template is designed to feel personal and premium.',
  templates_cta: 'Create Your Invite',
};

const TemplatesLive = lazy(() =>
  import('./TemplatesLiveSection').then(m => ({ default: m.TemplatesLiveSection }))
);

const themeList = [
  { id: 'luxury',    label: 'Classical'    },
  { id: 'sage',      label: 'Sage'         },
  { id: 'floral',    label: 'Floral'       },
  { id: 'embossed',  label: 'Embossed'     },
  { id: 'geometric', label: 'Geometric'    },
  { id: 'polaroid',  label: 'Polaroid'     },
  { id: 'sketch',    label: 'Illustration' },
];

// SSR / pre-hydration placeholder — static screenshots only, no invite renderer.
function MockupPlaceholder() {
  return (
    <div className="overflow-hidden">
      <div className="mx-auto bg-background rounded-xl border border-border shadow-2xl overflow-hidden flex" style={{ maxWidth: 920, height: 556 }}>
        <div className="w-full flex flex-col">
          <div className="flex items-center gap-2 px-4 bg-gray-200/80 border-b border-gray-300 flex-shrink-0" style={{ height: 36 }}>
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 mx-4">
              <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 text-center max-w-xs mx-auto">wedsy.co/invite/luxury</div>
            </div>
          </div>
          <div className="flex flex-1">
            <div className="hidden lg:block flex-shrink-0 border-r border-border bg-background-card" style={{ width: 240 }}>
              <div className="p-3 h-full">
                <p className="text-[10px] font-medium text-text-muted uppercase tracking-wider mb-3">Choose theme</p>
                <div className="grid grid-cols-2 gap-2">
                  {themeList.map(t => (
                    <div key={t.id} className={`rounded-lg overflow-hidden border ${t.id === 'luxury' ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}>
                      <div style={{ height: 80, backgroundImage: `url(/theme-previews/${t.id}.webp)`, backgroundSize: 'cover', backgroundPosition: 'top center' }} />
                      <div className="px-1.5 py-1 bg-background border-t border-border">
                        <p className="font-medium text-text-primary text-[10px] leading-tight truncate">{t.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex-1 bg-gray-100 flex items-center justify-center p-6">
              <div className="bg-gray-900 shadow-2xl overflow-hidden flex-shrink-0" style={{ borderRadius: 22, padding: 4, height: '100%', aspectRatio: '390/844' }}>
                <div className="bg-white overflow-hidden h-full" style={{ borderRadius: 18 }}>
                  <img
                    src="/theme-previews/luxury.webp"
                    alt="Classical wedding invitation preview"
                    width={780}
                    height={1688}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TemplatesSection({ strings }: { strings?: TemplatesStrings }) {
  const s = strings ?? EN_DEFAULTS;
  const sectionRef = useRef<HTMLElement>(null);
  const [showLive, setShowLive] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    // Only trigger when the section is actually in the viewport — Lighthouse doesn't
    // scroll so this never fires during a perf audit, keeping the invite renderer
    // chunk out of the critical load path.
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShowLive(true); obs.disconnect(); } },
      { rootMargin: '-80px', threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="templates" ref={sectionRef} className="py-20 md:py-32 bg-background-card relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-12"
        >
          <motion.span variants={fadeInUp} className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
            {s.templates_badge}
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4" style={displayFont}>
            {s.templates_h2_pre}<span className="text-primary">{s.templates_h2_highlight}</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto">
            {s.templates_body}
          </motion.p>
        </motion.div>

        {/* Live mockup loads only when user scrolls to this section */}
        {showLive ? (
          <Suspense fallback={<MockupPlaceholder />}>
            <TemplatesLive />
          </Suspense>
        ) : (
          <MockupPlaceholder />
        )}

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mt-10"
        >
          <Link to="/create" className="btn btn-primary hover:shadow-lg">
            {s.templates_cta}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
